import { Alert, Col, Input, message, Row, Spin } from 'antd';
import AddressFields from 'components/AddressFields';
import Button from 'components/Button';
import Card from 'components/Card/Card';
import Form from 'components/Form';
import FormItem from 'components/FormItem';
import PhoneNumber from 'components/PhoneNumber';
import PhoneNumberContainer from 'components/PhoneNumberContainer';
import { Banner } from 'components/ui/Banner';
import PageContainer from 'components/PageContainer/PageContainer';
import PageTitleBar from 'components/PageTitleBar/PageTitleBar';
import TabularList from 'components/TabularList/TabularList';
import FormattedAddress from 'components/FormattedAddress/FormattedAddress';
import { actions, selectors } from 'features/auth';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { providerService } from 'services';
import { getFormattedPhoneNumber, getPhoneEXTValidator, getPhoneNumberValidator, getStringValidator } from 'utils';
import { CardFooter } from 'components/Card/Card';
import BankingProfiles from './__components/BankingProfiles';
import TaxProfiles from './__components/TaxProfiles';
import { EMDASH, BUSINESS_NAME_REGEX } from 'constants';
import { ArrowLeft, Locked } from '@carbon/icons-react';

function ReadOnlyBadge() {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-solid border-gray-200"
      data-testid="readOnlyBadge"
    >
      <Locked size={14} />
      Read-only
    </span>
  );
}

function EditableBadge() {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-solid border-blue-200"
      data-testid="editableBadge"
    >
      Editable
    </span>
  );
}

export default function BusinessProfile() {
  const history = useHistory();
  const [form] = Form.useForm();

  const billingAddressRef = useRef();
  const billingAddressKey = 'address';

  // Organization data
  const dispatch = useDispatch();
  const organization = useSelector(selectors.selectOrganization);
  const [organizationUpdating, setOrganizationUpdating] = useState(false);
  const [bankingProfiles, setBankingProfiles] = useState([]);
  const [showBankingValidation, setShowBankingValidation] = useState(false);
  const [taxProfiles, setTaxProfiles] = useState([]);
  const [showTaxValidation, setShowTaxValidation] = useState(false);

  const handleBankingProfilesChange = (profiles) => {
    setBankingProfiles(profiles);
    if (profiles.length > 0) {
      setShowBankingValidation(false);
    }
  };

  const handleTaxProfilesChange = (profiles) => {
    setTaxProfiles(profiles);
    if (profiles.length > 0) {
      setShowTaxValidation(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      businessLegalName: organization?.businessLegalName,
      businessPhone: organization?.businessPhone,
      businessPhoneExt: organization?.businessPhoneExt,
      businessContact: {
        firstName: organization?.businessContact?.firstName,
        lastName: organization?.businessContact?.lastName,
        position: organization?.businessContact?.position,
        email: organization?.businessContact?.email,
        phone: organization?.businessContact?.phone,
        phoneExt: organization?.businessContact?.phoneExt,
      },
      address: organization?.address,
    });
  }, [form, organization]);

  const isDisabled = useMemo(() => !!organization && !!organization?.profileValid, [organization]);

  const [cardOpen, setCardOpen] = useState({
    basicDetails: false,
    contact: false,
    address: false,
    tax: true,
    banking: true,
  });

  return (
    <PageContainer>
      <Spin spinning={organization === null}>
        <PageTitleBar
          title="Organization profile"
          subtitle={
            isDisabled ? (
              <span className="text-[#b9c1ff]">
                Status: <span className="text-[#a7d6b0]">Complete</span>
              </span>
            ) : (
              <span className="text-[#ffb84d]">Status: Required</span>
            )
          }
          className="mb-8"
        />

        <div className="px-4 md:px-0">
          {isDisabled && (
            <Alert
              type="success"
              message="Locked"
              description={
                <div className="flex flex-col gap-2">
                  <p>
                    To make changes, contact your coordinator at{' '}
                    <a
                      href="mailto:providerservices@usa.childcareaware.org"
                      className="!text-black font-semibold !underline"
                    >
                      providerservices@usa.childcareaware.org
                    </a>
                  </p>

                  <p>You can still add new tax and banking profiles below.</p>
                </div>
              }
              className="mb-8"
              showIcon
              icon={<Locked size={24} className="mt-[-1px]" />}
            />
          )}

          {!isDisabled && (
            <Alert
              message={<span className="text-2xl font-bold">Welcome!</span>}
              description={
                <>
                  Complete your organization profile to continue applying as a participating provider in the{' '}
                  <a
                    href="https://www.childcareaware.org/fee-assistancerespite/feeassistancerespiteproviders/feeassistance/"
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="!underline"
                  >
                    Fee Assistance Program
                  </a>
                  .
                </>
              }
              type="info"
              className="mb-8"
            />
          )}

          <Form
            onFinish={async (values) => {
              try {
                // Validate banking profiles
                if (bankingProfiles.length === 0) {
                  message.error('Please add a banking profile before saving');
                  return;
                }

                if (taxProfiles.length === 0) {
                  message.error('Please add a tax profile before saving');
                  return;
                }

                setOrganizationUpdating(true);

                // Only submit fields that are part of this form — banking and tax are
                // managed separately by BankingProfiles and TaxProfiles components.
                const { businessLegalName, businessPhone, businessPhoneExt, businessContact, address } = values;

                // call the api to save the data
                const _updatedOrganization = await providerService.updateOrganization(organization?.id, {
                  businessLegalName,
                  businessPhone,
                  businessPhoneExt,
                  businessContact,
                  address,
                });
                const _organizationValidity = await providerService.getOrganizationValidity(organization?.id);

                // update the organization in the store
                dispatch(
                  actions.setOrganization({
                    ..._updatedOrganization,
                    profileValid: _organizationValidity?.profileComplete,
                  }),
                );

                // show the success message
                message.success('Organization profile saved');

                // redirect to the next page
                history.push('/provider/locations-and-programs');
              } catch (error) {
                // show the error message
                message.error('Unable to save the organization profile');
              } finally {
                setOrganizationUpdating(false);
              }
            }}
            name="business-profile"
            form={form}
            layout="vertical"
            className="mt-4"
          >
            {isDisabled && (
              <Card
                title={<Banner variant="card-title" question="Basic details" className="my-8" />}
                extra={<ReadOnlyBadge />}
                className="mb-8"
                data-testid="basicDetailsCard"
                noBodyPadding
                collapsible
                open={cardOpen.basicDetails}
                onOpenChange={(v) => setCardOpen((prev) => ({ ...prev, basicDetails: v }))}
              >
                <TabularList
                  indented
                  data={[
                    {
                      title: 'Organization name',
                      value: organization?.businessLegalName || '-',
                    },
                  ]}
                />
              </Card>
            )}

            <Card
              title={
                <Banner
                  variant="card-title"
                  question="Organization contact"
                  guidance={
                    !isDisabled
                      ? 'The organization contact is the person who will manage payments and remittance.'
                      : undefined
                  }
                  className="my-8"
                />
              }
              extra={isDisabled ? <ReadOnlyBadge /> : undefined}
              className="mb-8"
              data-testid="businessContactCard"
              noBodyPadding={isDisabled}
              collapsible={isDisabled}
              open={isDisabled ? cardOpen.contact : undefined}
              onOpenChange={isDisabled ? (v) => setCardOpen((prev) => ({ ...prev, contact: v })) : undefined}
            >
              {isDisabled ? (
                <TabularList
                  indented
                  data={[
                    {
                      title: 'First name',
                      value: organization?.businessContact?.firstName || '-',
                    },
                    {
                      title: 'Last name',
                      value: organization?.businessContact?.lastName || '-',
                    },
                    {
                      title: 'Role/Position',
                      value: organization?.businessContact?.position || '-',
                    },
                    {
                      title: 'Email',
                      value: organization?.businessContact?.email || '-',
                    },
                    {
                      title: 'Phone number',
                      value: organization?.businessContact?.phone
                        ? getFormattedPhoneNumber(
                            organization.businessContact.phone,
                            organization.businessContact.phoneExt,
                          )
                        : '-',
                    },
                  ]}
                />
              ) : (
                <>
                  <Row gutter={[20, 0]} className="form-row">
                    <Col xs={24} sm={22} md={12}>
                      <FormItem
                        name={['businessContact', 'firstName']}
                        label="First Name"
                        rules={[{ required: true, message: 'First name is required' }]}
                      >
                        <Input aria-label="First name" data-testid="firstName" />
                      </FormItem>
                    </Col>

                    <Col xs={24} sm={22} md={12}>
                      <FormItem
                        name={['businessContact', 'lastName']}
                        label="Last Name"
                        rules={[{ required: true, message: 'Last name is required' }]}
                      >
                        <Input aria-label="Last name" data-testid="lastName" />
                      </FormItem>
                    </Col>
                  </Row>

                  <Col xs={18} sm={15} md={15}>
                    <FormItem
                      name={['businessContact', 'position']}
                      label="Role/Position"
                      rules={[{ required: true, message: 'Role/position is required' }]}
                    >
                      <Input aria-label="position" data-testid="position" />
                    </FormItem>

                    <FormItem
                      name={['businessContact', 'email']}
                      label="Email"
                      rules={[
                        { required: true, message: 'Email is required' },
                        { type: 'email', message: 'Email address is invalid' },
                      ]}
                    >
                      <Input aria-label="business email" data-testid="businessEmail" />
                    </FormItem>
                  </Col>

                  <Col xs={18} sm={15} md={12}>
                    <PhoneNumberContainer
                      phoneNumber={
                        <FormItem
                          name={['businessContact', 'phone']}
                          label="Phone number"
                          rules={[
                            { required: true, message: 'Phone number is required' },
                            getPhoneNumberValidator('Invalid Phone Number'),
                          ]}
                          className="mb-0"
                        >
                          <PhoneNumber aria-label="phone number" disabled={isDisabled} data-testid="businessContact" />
                        </FormItem>
                      }
                      extension={
                        <FormItem
                          name={['businessContact', 'phoneExt']}
                          label="Extension"
                          rules={[getPhoneEXTValidator('Invalid Extension')]}
                          className="mb-0"
                        >
                          <Input aria-label="phone extension" data-testid="businessContactExt" />
                        </FormItem>
                      }
                    />
                  </Col>
                </>
              )}
            </Card>

            <Card
              title={
                <Banner
                  variant="card-title"
                  question="Organization address and phone"
                  guidance={
                    !isDisabled
                      ? 'Add your organization mailing address. This information must match your IRS Form W-9.'
                      : undefined
                  }
                  className="my-8"
                />
              }
              extra={isDisabled ? <ReadOnlyBadge /> : undefined}
              className="mb-8"
              data-testid="businessAddressAndPhoneCard"
              noBodyPadding={isDisabled}
              collapsible={isDisabled}
              open={isDisabled ? cardOpen.address : undefined}
              onOpenChange={isDisabled ? (v) => setCardOpen((prev) => ({ ...prev, address: v })) : undefined}
            >
              {isDisabled ? (
                <TabularList
                  indented
                  data={[
                    {
                      title: 'Address',
                      value: organization?.address ? <FormattedAddress address={organization.address} /> : '-',
                    },
                    {
                      title: 'Phone number',
                      value: organization?.businessPhone
                        ? getFormattedPhoneNumber(organization?.businessPhone, organization.businessPhoneExt)
                        : EMDASH,
                    },
                  ]}
                />
              ) : (
                <>
                  <Col xs={18} sm={15} md={15}>
                    <FormItem
                      name="businessLegalName"
                      label="Business Legal Name"
                      rules={[
                        { required: true, message: 'Business legal name is required' },
                        { type: 'string', message: 'Invalid business legal name' },
                        { max: 256, message: 'Business legal name is too long' },
                        { min: 3, message: 'Business legal name is too short' },
                        {
                          pattern: BUSINESS_NAME_REGEX,
                          message: 'Business legal name is invalid',
                        },
                        {
                          validator: getStringValidator('Business legal name is invalid'),
                        },
                      ]}
                      validateFirst
                    >
                      <Input data-testid="businessLegalName" aria-label="business legal name" />
                    </FormItem>
                  </Col>

                  <AddressFields
                    id="billing-address"
                    form={form}
                    searchRef={billingAddressRef}
                    addressKey={billingAddressKey}
                    readOnly={isDisabled}
                    data-testid="businessMailingAddress"
                  />

                  <Col xs={18} sm={15} md={12}>
                    <PhoneNumberContainer
                      phoneNumber={
                        <FormItem
                          name={['businessPhone']}
                          label="Phone number"
                          rules={[
                            { required: true, message: 'Phone number is required' },
                            getPhoneNumberValidator('Invalid Phone Number'),
                          ]}
                          className="m
                       b    -0"
                        >
                          <PhoneNumber
                            aria-label="phone number"
                            disabled={isDisabled}
                            data-testid="businessPhoneNumber"
                          />
                        </FormItem>
                      }
                      extension={
                        <FormItem
                          name={['businessPhoneExt']}
                          label="Extension"
                          rules={[getPhoneEXTValidator('Invalid Extension.')]}
                          className="mb-0"
                        >
                          <Input aria-label="phone extension" data-testid="businessPhoneNumberExt" />
                        </FormItem>
                      }
                    />
                  </Col>
                </>
              )}
            </Card>
          </Form>

          <BankingProfiles
            organization={organization}
            onProfilesChange={handleBankingProfilesChange}
            collapsible={isDisabled}
            open={isDisabled ? cardOpen.banking : undefined}
            onOpenChange={isDisabled ? (v) => setCardOpen((prev) => ({ ...prev, banking: v })) : undefined}
            extra={isDisabled ? <EditableBadge /> : undefined}
          />

          <TaxProfiles
            organization={organization}
            onProfilesChange={handleTaxProfilesChange}
            collapsible={isDisabled}
            open={isDisabled ? cardOpen.tax : undefined}
            onOpenChange={isDisabled ? (v) => setCardOpen((prev) => ({ ...prev, tax: v })) : undefined}
            extra={isDisabled ? <EditableBadge /> : undefined}
          />

          {isDisabled && (
            <div className="flex justify-start mb-4">
              <Button
                size="large"
                icon={<ArrowLeft className="ml-[-4px] mb-[-1px]" />}
                onClick={() => history.push('/provider/locations-and-programs')}
                data-testid="back-to-dashboard"
                className="px-8"
                color="default"
                variant="filled"
              >
                Back to dashboard
              </Button>
            </div>
          )}

          {!isDisabled && (
            <Card noBodyPadding className="mb-4" data-testid="readyToSaveCard">
              <Banner
                variant="card-title"
                question="Ready to save?"
                guidance='Review your answers carefully before saving. After you click "Save profile", you will need to email Provider Services to make changes.'
                className="my-8 mx-7"
              />

              <div className="px-7">
                {showBankingValidation && bankingProfiles.length === 0 && (
                  <div className="text-red-500 mb-8">Add a banking profile before saving</div>
                )}
                {showTaxValidation && taxProfiles.length === 0 && (
                  <div className="text-red-500 mb-8">Add a tax profile before saving</div>
                )}
              </div>

              <CardFooter className="p-7">
                <div className="flex justify-end">
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => {
                      setShowBankingValidation(true);
                      setShowTaxValidation(true);
                      form.submit();
                    }}
                    loading={organizationUpdating}
                    data-testid="save-profile"
                    className="px-8"
                  >
                    Save
                  </Button>
                </div>
              </CardFooter>
            </Card>
          )}
        </div>
      </Spin>
    </PageContainer>
  );
}
